/**
 * iter318 wave 14 — Super Admin Website Management (Phase B).
 *
 * Tabs:
 *   • Navigation   — top-nav + footer links (placement discriminator)
 *   • Footer       — footer column groups
 *   • Home Sections — Home page section content
 *
 * Backed by /api/super-admin/website/{navigation, footer-groups, sections}.
 * Public read endpoint (/api/public/website/content) is not consumed here —
 * Phase C wires the marketing Home page to it.
 */
import { useEffect, useRef, useState } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import { Trash2, Plus, Save, Eye, EyeOff, X, Upload, ChevronDown, ChevronRight, ArrowRightLeft, Copy } from 'lucide-react';
import SectionRenderer from '../public/vyomos/SectionRenderer';
import { MediaLibraryPanel, HtmlField } from './WebsiteMediaLibrary';
import { GlobalThemePanel } from './WebsiteGlobalTheme';
import { PageSeoEditor, SeoToggle } from './PageSeoEditor';
import WebsiteSearchPanel from './WebsiteSearchPanel';
import { CookieNoticeSettings } from './CookieNoticeSettings';

const TABS = [
  { key: 'pages', label: 'Pages' },
  { key: 'search', label: 'SEO & Search' },
  { key: 'navigation', label: 'Navigation' },
  { key: 'footer', label: 'Footer Groups' },
  { key: 'sections', label: 'Page Sections' },
  { key: 'pricing', label: 'Pricing' },
  { key: 'theme', label: 'Global Theme (CSS)' },
  { key: 'media', label: 'Media Library' },
  { key: 'settings', label: 'Site Settings' },
];

const KIND_DEFAULT_FIELDS = {
  hero: ['eyebrow', 'title', 'subtitle', 'ctas'],
  text: ['eyebrow', 'title', 'body', 'tint'],
  grid: ['eyebrow', 'title', 'subtitle', 'cards', 'tint'],
  cta: ['title', 'subtitle', 'ctas', 'tint'],
  faq: ['title', 'cards', 'tint'],
  stats: ['title', 'cards', 'tint'],
  html: ['html'],
  builtin: ['variant'],
};

const PAGES = ['home', 'product', 'solution', 'features', 'ai', 'pricevyomos', 'demovyomos', 'contactus', 'about', 'privacy', 'terms'];
const KINDS = Object.keys(KIND_DEFAULT_FIELDS);
const TINTS = ['light', 'tint', 'dark'];

export default function SuperAdminWebsite() {
  // iter343 — land on "Page Sections" so the most-used content editor is
  // front-and-centre (users kept missing where to edit page content).
  const [tab, setTab] = useState('sections');
  const [importing, setImporting] = useState(false);
  const importContent = async () => {
    if (!window.confirm('Populate the CMS with the current vyomos.io copy? Existing rows are kept — only missing content is added.')) return;
    setImporting(true);
    try {
      const { data } = await api.post('/super-admin/website/import-default-content');
      toast.success(`Imported ${data.inserted} item(s); ${data.already_present} already present. Refresh the tabs to see them.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import failed');
    } finally { setImporting(false); }
  };
  return (
    <div className="max-w-6xl" data-testid="super-admin-website">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight mb-1">Website Management</h1>
          <p className="text-sm text-slate-600">
            Edit the public marketing site&apos;s navigation, footer, and page sections.
            Content published here appears on <code className="text-xs px-1 py-0.5 rounded bg-slate-100">www.vyomos.io</code>.
          </p>
        </div>
        <button
          onClick={importContent}
          disabled={importing}
          data-testid="website-import-content-btn"
          className="shrink-0 inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-medium px-3.5 py-2 rounded"
        >
          {importing ? 'Importing…' : 'Import current content'}
        </button>
      </div>

      <div className="flex gap-2 border-b border-slate-200 mb-6 overflow-x-auto flex-nowrap -mx-1 px-1" data-testid="website-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`website-tab-${t.key}`}
            className={
              tab === t.key
                ? 'px-4 py-2 text-sm font-semibold border-b-2 border-slate-900 text-slate-900 whitespace-nowrap shrink-0'
                : 'px-4 py-2 text-sm text-slate-500 hover:text-slate-900 border-b-2 border-transparent whitespace-nowrap shrink-0'
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'pages' && <PagesPanel />}
      {tab === 'search' && <WebsiteSearchPanel />}
      {tab === 'navigation' && <NavigationPanel />}
      {tab === 'footer' && <FooterGroupsPanel />}
      {tab === 'sections' && <SectionsPanel />}
      {tab === 'pricing' && <PricingPanel />}
      {tab === 'theme' && <GlobalThemePanel />}
      {tab === 'media' && <MediaLibraryPanel />}
      {tab === 'settings' && <SiteSettingsPanel />}
    </div>
  );
}

// ------------------------------------------------------------------
// Navigation panel
// ------------------------------------------------------------------

function NavigationPanel() {
  const [items, setItems] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [navRes, grpRes] = await Promise.all([
        api.get('/super-admin/website/navigation'),
        api.get('/super-admin/website/footer-groups'),
      ]);
      setItems(navRes.data.items || []);
      setGroups(grpRes.data.groups || []);
    } catch (_e) {
      toast.error('Failed to load navigation');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async (payload) => {
    try {
      await api.post('/super-admin/website/navigation', payload);
      toast.success('Nav item added');
      setCreating(false);
      load();
    } catch (_e) {
      toast.error('Failed to add nav item');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-600">
          Top nav items appear in the marketing site header. Footer links belong to a footer group.
        </p>
        <button
          onClick={() => setCreating(true)}
          data-testid="nav-add-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium"
        >
          <Plus className="w-4 h-4" /> Add nav item
        </button>
      </div>

      {creating && (
        <NavForm groups={groups} onSubmit={create} onCancel={() => setCreating(false)} />
      )}

      {loading ? (
        <div className="text-sm text-slate-400 py-8 text-center">Loading…</div>
      ) : (
        <div className="space-y-6">
          {['top', 'footer'].map((placement) => {
            const filtered = items.filter((i) => i.placement === placement);
            return (
              <div key={placement}>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                  {placement === 'top' ? 'Top nav' : 'Footer links'} ({filtered.length})
                </div>
                {filtered.length === 0 ? (
                  <div className="text-sm text-slate-400 border border-dashed border-slate-200 rounded p-4">No items yet.</div>
                ) : (
                  <div className="space-y-2">
                    {filtered.sort((a, b) => a.order - b.order).map((it) => (
                      <NavRow key={it.id} item={it} groups={groups} onSaved={load} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function NavRow({ item, groups, onSaved }) {
  const [row, setRow] = useState(item);
  const [dirty, setDirty] = useState(false);
  const set = (k, v) => { setRow({ ...row, [k]: v }); setDirty(true); };

  const save = async () => {
    try {
      await api.put(`/super-admin/website/navigation/${item.id}`, {
        label: row.label, href: row.href, order: Number(row.order),
        placement: row.placement, footer_group_id: row.footer_group_id || null,
        external: !!row.external, enabled: !!row.enabled,
      });
      toast.success('Saved');
      setDirty(false);
      onSaved();
    } catch (_e) { toast.error('Save failed'); }
  };
  const del = async () => {
    if (!window.confirm(`Delete "${item.label}"?`)) return;
    try {
      await api.delete(`/super-admin/website/navigation/${item.id}`);
      toast.success('Deleted');
      onSaved();
    } catch (_e) { toast.error('Delete failed'); }
  };

  return (
    <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg p-3" data-testid={`nav-row-${item.id}`}>
      <input value={row.label} onChange={(e) => set('label', e.target.value)} className="input w-40" placeholder="Label" />
      <input value={row.href} onChange={(e) => set('href', e.target.value)} className="input flex-1" placeholder="/path or https://…" />
      {row.placement === 'footer' && (
        <select value={row.footer_group_id || ''} onChange={(e) => set('footer_group_id', e.target.value || null)} className="input w-40">
          <option value="">— No group —</option>
          {groups.map((g) => <option key={g.id} value={g.id}>{g.title}</option>)}
        </select>
      )}
      <input type="number" value={row.order} onChange={(e) => set('order', e.target.value)} className="input w-16" title="Order" />
      <button
        onClick={() => set('enabled', !row.enabled)}
        className={row.enabled ? 'p-1.5 text-emerald-600 hover:bg-emerald-50 rounded' : 'p-1.5 text-slate-400 hover:bg-slate-100 rounded'}
        title={row.enabled ? 'Published' : 'Hidden'}
      >
        {row.enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
      </button>
      <button
        onClick={save}
        disabled={!dirty}
        className={dirty ? 'p-1.5 text-blue-600 hover:bg-blue-50 rounded' : 'p-1.5 text-slate-300'}
        title="Save"
        data-testid={`nav-save-${item.id}`}
      >
        <Save className="w-4 h-4" />
      </button>
      <button onClick={del} className="p-1.5 text-rose-600 hover:bg-rose-50 rounded" title="Delete" data-testid={`nav-delete-${item.id}`}>
        <Trash2 className="w-4 h-4" />
      </button>
      <style>{`.input { padding:6px 10px;border:1px solid #E2E8F0;border-radius:8px;font-size:13px;background:#fff;color:#0f172a }`}</style>
    </div>
  );
}

function NavForm({ groups, onSubmit, onCancel }) {
  const [f, setF] = useState({ placement: 'top', label: '', href: '', order: 0, footer_group_id: '', external: false, enabled: true });
  const set = (k, v) => setF({ ...f, [k]: v });
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSubmit({ ...f, order: Number(f.order), footer_group_id: f.footer_group_id || null }); }}
      className="mb-4 bg-slate-50 border border-slate-200 rounded-lg p-4 flex flex-wrap items-end gap-3"
      data-testid="nav-create-form"
    >
      <div>
        <label className="block text-xs text-slate-600 mb-1">Placement</label>
        <select value={f.placement} onChange={(e) => set('placement', e.target.value)} className="input">
          <option value="top">Top nav</option>
          <option value="footer">Footer</option>
        </select>
      </div>
      <div>
        <label className="block text-xs text-slate-600 mb-1">Label</label>
        <input required value={f.label} onChange={(e) => set('label', e.target.value)} className="input w-40" data-testid="nav-form-label" />
      </div>
      <div>
        <label className="block text-xs text-slate-600 mb-1">Href</label>
        <input required value={f.href} onChange={(e) => set('href', e.target.value)} className="input w-56" data-testid="nav-form-href" />
      </div>
      {f.placement === 'footer' && (
        <div>
          <label className="block text-xs text-slate-600 mb-1">Group</label>
          <select value={f.footer_group_id} onChange={(e) => set('footer_group_id', e.target.value)} className="input w-40">
            <option value="">— No group —</option>
            {groups.map((g) => <option key={g.id} value={g.id}>{g.title}</option>)}
          </select>
        </div>
      )}
      <div>
        <label className="block text-xs text-slate-600 mb-1">Order</label>
        <input type="number" value={f.order} onChange={(e) => set('order', e.target.value)} className="input w-20" />
      </div>
      <button type="submit" className="px-3 py-1.5 rounded bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium" data-testid="nav-form-submit">Add</button>
      <button type="button" onClick={onCancel} className="px-3 py-1.5 rounded text-slate-600 hover:bg-slate-100 text-sm"><X className="w-4 h-4" /></button>
    </form>
  );
}

// ------------------------------------------------------------------
// Footer Groups panel
// ------------------------------------------------------------------

function FooterGroupsPanel() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/super-admin/website/footer-groups');
      setGroups(data.groups || []);
    } catch (_e) { toast.error('Failed to load groups'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post('/super-admin/website/footer-groups', { title, order: groups.length + 1 });
      toast.success('Group added');
      setTitle('');
      setCreating(false);
      load();
    } catch (_e) { toast.error('Failed'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-600">Footer columns. Each column groups a set of footer links.</p>
        <button onClick={() => setCreating(true)} data-testid="group-add-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium">
          <Plus className="w-4 h-4" /> Add group
        </button>
      </div>
      {creating && (
        <form onSubmit={create} className="mb-4 bg-slate-50 border border-slate-200 rounded-lg p-4 flex items-end gap-3">
          <div>
            <label className="block text-xs text-slate-600 mb-1">Title</label>
            <input required value={title} onChange={(e) => setTitle(e.target.value)} className="input w-56" data-testid="group-form-title" />
          </div>
          <button type="submit" className="px-3 py-1.5 rounded bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium" data-testid="group-form-submit">Add</button>
          <button type="button" onClick={() => setCreating(false)} className="px-3 py-1.5 rounded text-slate-600 hover:bg-slate-100 text-sm"><X className="w-4 h-4" /></button>
        </form>
      )}
      {loading ? (
        <div className="text-sm text-slate-400 py-8 text-center">Loading…</div>
      ) : groups.length === 0 ? (
        <div className="text-sm text-slate-400 border border-dashed border-slate-200 rounded p-6 text-center">No footer groups yet.</div>
      ) : (
        <div className="space-y-2">
          {groups.sort((a, b) => a.order - b.order).map((g) => <FooterGroupRow key={g.id} group={g} onSaved={load} />)}
        </div>
      )}
      <style>{`.input { padding:6px 10px;border:1px solid #E2E8F0;border-radius:8px;font-size:13px;background:#fff;color:#0f172a }`}</style>
    </div>
  );
}

function FooterGroupRow({ group, onSaved }) {
  const [row, setRow] = useState(group);
  const [dirty, setDirty] = useState(false);
  const set = (k, v) => { setRow({ ...row, [k]: v }); setDirty(true); };
  const save = async () => {
    try {
      await api.put(`/super-admin/website/footer-groups/${group.id}`, {
        title: row.title, order: Number(row.order), enabled: !!row.enabled,
      });
      toast.success('Saved');
      setDirty(false);
      onSaved();
    } catch (_e) { toast.error('Save failed'); }
  };
  const del = async () => {
    if (!window.confirm(`Delete group "${group.title}"? Links inside will be detached.`)) return;
    try {
      await api.delete(`/super-admin/website/footer-groups/${group.id}`);
      toast.success('Deleted');
      onSaved();
    } catch (_e) { toast.error('Delete failed'); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3" data-testid={`group-row-${group.id}`}>
      <div className="flex items-center gap-2">
        <input value={row.title} onChange={(e) => set('title', e.target.value)} className="input flex-1 font-medium" />
        <input type="number" value={row.order} onChange={(e) => set('order', e.target.value)} className="input w-16" title="Order" />
        <button onClick={() => set('enabled', !row.enabled)}
          className={row.enabled ? 'p-1.5 text-emerald-600 hover:bg-emerald-50 rounded' : 'p-1.5 text-slate-400 hover:bg-slate-100 rounded'}
          title={row.enabled ? 'Published' : 'Hidden'}>
          {row.enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
        </button>
        <button onClick={save} disabled={!dirty} className={dirty ? 'p-1.5 text-blue-600 hover:bg-blue-50 rounded' : 'p-1.5 text-slate-300'}>
          <Save className="w-4 h-4" />
        </button>
        <button onClick={del} className="p-1.5 text-rose-600 hover:bg-rose-50 rounded" data-testid={`group-delete-${group.id}`}><Trash2 className="w-4 h-4" /></button>
      </div>
      {group.links && group.links.length > 0 && (
        <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-600">
          <div className="font-semibold uppercase tracking-wider text-slate-500 mb-1">Links in this group ({group.links.length})</div>
          <ul className="list-disc list-inside space-y-0.5">
            {group.links.map((l) => <li key={l.id}>{l.label} → <code>{l.href}</code></li>)}
          </ul>
          <p className="text-slate-400 mt-1">Manage links from the Navigation tab.</p>
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------
// Sections panel
// ------------------------------------------------------------------

function SectionsPanel() {
  const [sections, setSections] = useState([]);
  const [page, setPage] = useState('home');
  const [pageOptions, setPageOptions] = useState(PAGES);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  // iter335e — live preview: a working copy of each section keyed by id,
  // updated as the operator types so the right-hand pane re-renders live.
  const [preview, setPreview] = useState({});

  // iter336 — dropdown lists all pages (system + custom) from the CMS.
  useEffect(() => {
    api.get('/super-admin/website/pages')
      .then(({ data }) => {
        const slugs = (data.pages || []).map((p) => p.slug);
        if (slugs.length) setPageOptions(slugs);
      })
      .catch(() => toast.error('Could not load the page list; showing built-in pages only.'));
  }, []);

  const latestPage = useRef(page);
  const load = async () => {
    latestPage.current = page;
    const want = page;
    setLoading(true);
    try {
      const { data } = await api.get('/super-admin/website/sections', { params: { page } });
      if (latestPage.current !== want) return;  // a newer page was picked meanwhile
      const rows = data.sections || [];
      setSections(rows);
      setPreview(Object.fromEntries(rows.map((s) => [s.id, s])));
    } catch (_e) { toast.error('Failed to load sections'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [page]);

  const onLiveChange = (id, patch) => {
    setPreview((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), ...patch } }));
  };

  const create = async (payload) => {
    try {
      await api.post('/super-admin/website/sections', { ...payload, page });
      toast.success('Section added');
      setCreating(false);
      load();
    } catch (_e) { toast.error('Failed to add section'); }
  };

  const previewSections = Object.values(preview)
    .filter((s) => s.enabled !== false)
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div>
      {/* iter343 — plain-language guide so operators know where to edit
          content and how to move a section between pages. */}
      <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900" data-testid="sections-help-banner">
        <p className="font-semibold mb-1">Editing page content</p>
        <ol className="list-decimal list-inside space-y-0.5 text-[13px] text-blue-800">
          <li>Pick the page you want to edit from the <strong>Page</strong> dropdown below.</li>
          <li>Click a section&apos;s <strong>Edit</strong> button to open it and change its heading, text, cards, and buttons.</li>
          <li>To move a section to a different page, open it and use the <strong>Move to page</strong> dropdown at the bottom.</li>
          <li>Press <strong>Save</strong> on the section — changes go live on the public site on its next load.</li>
          <li>Use the <strong>copy icon</strong> on any section to duplicate it, then edit or move the copy.</li>
        </ol>
      </div>

      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <label className="text-sm text-slate-600">Page:</label>
          <select value={page} onChange={(e) => setPage(e.target.value)} className="input" data-testid="sections-page-select">
            {pageOptions.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <button onClick={() => setCreating(true)} data-testid="section-add-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium">
          <Plus className="w-4 h-4" /> Add section
        </button>
      </div>
      {creating && <SectionForm onSubmit={create} onCancel={() => setCreating(false)} />}

      <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-6">
        {/* Editor column */}
        <div>
          {loading ? (
            <div className="text-sm text-slate-400 py-8 text-center">Loading…</div>
          ) : sections.length === 0 ? (
            <div className="text-sm text-slate-400 border border-dashed border-slate-200 rounded p-6 text-center">
              No sections on <strong>{page}</strong> yet.
            </div>
          ) : (
            <div className="space-y-2">
              {sections.sort((a, b) => a.order - b.order).map((s) => (
                <SectionRow key={s.id} section={s} onSaved={load} onLiveChange={onLiveChange} pageOptions={pageOptions} currentPage={page} />
              ))}
            </div>
          )}
        </div>

        {/* Live preview column */}
        <div className="hidden lg:block">
          <div className="sticky top-4">
            <div className="flex items-center gap-2 mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
              <Eye className="w-3.5 h-3.5" /> Live preview · {page}
            </div>
            <div className="border border-slate-200 rounded-lg overflow-hidden bg-white max-h-[75vh] overflow-y-auto"
                 data-testid="sections-live-preview">
              {previewSections.length === 0 ? (
                <div className="text-sm text-slate-400 p-8 text-center">Nothing published to preview.</div>
              ) : (
                <div style={{ zoom: 0.72 }}>
                  {previewSections.map((s) => (
                    <SectionRenderer key={s.id} section={s} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <style>{`.input { padding:6px 10px;border:1px solid #E2E8F0;border-radius:8px;font-size:13px;background:#fff;color:#0f172a }`}</style>
    </div>
  );
}

function SectionForm({ onSubmit, onCancel }) {
  const [f, setF] = useState({ section_key: '', kind: 'text', title: '', order: 0, tint: 'light', enabled: true });
  const set = (k, v) => setF({ ...f, [k]: v });
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit({ ...f, order: Number(f.order) }); }}
      className="mb-4 bg-slate-50 border border-slate-200 rounded-lg p-4 flex flex-wrap items-end gap-3" data-testid="section-create-form">
      <div>
        <label className="block text-xs text-slate-600 mb-1">Key</label>
        <input required value={f.section_key} onChange={(e) => set('section_key', e.target.value)} className="input w-40" placeholder="hero, problem…" data-testid="section-form-key" />
      </div>
      <div>
        <label className="block text-xs text-slate-600 mb-1">Kind</label>
        <select value={f.kind} onChange={(e) => set('kind', e.target.value)} className="input">
          {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-xs text-slate-600 mb-1">Title</label>
        <input value={f.title} onChange={(e) => set('title', e.target.value)} className="input w-64" data-testid="section-form-title" />
      </div>
      <div>
        <label className="block text-xs text-slate-600 mb-1">Tint</label>
        <select value={f.tint} onChange={(e) => set('tint', e.target.value)} className="input">
          {TINTS.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-xs text-slate-600 mb-1">Order</label>
        <input type="number" value={f.order} onChange={(e) => set('order', e.target.value)} className="input w-20" />
      </div>
      <button type="submit" className="px-3 py-1.5 rounded bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium" data-testid="section-form-submit">Add</button>
      <button type="button" onClick={onCancel} className="px-3 py-1.5 rounded text-slate-600 hover:bg-slate-100 text-sm"><X className="w-4 h-4" /></button>
    </form>
  );
}

function SectionRow({ section, onSaved, onLiveChange, pageOptions = [], currentPage }) {
  const [row, setRow] = useState(section);
  const [dirty, setDirty] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const set = (k, v) => {
    setRow({ ...row, [k]: v });
    setDirty(true);
    if (onLiveChange) onLiveChange(section.id, { [k]: v });
  };
  const moveToPage = async (newPage) => {
    if (!newPage || newPage === currentPage) return;
    if (!window.confirm(`Move section "${row.section_key}" to the "${newPage}" page?`)) return;
    try {
      await api.put(`/super-admin/website/sections/${section.id}`, { page: newPage });
      toast.success(`Moved to ${newPage}`);
      onSaved();
    } catch (_e) { toast.error('Move failed'); }
  };

  const save = async () => {
    try {
      const payload = {
        section_key: row.section_key, kind: row.kind, order: Number(row.order),
        eyebrow: row.eyebrow || null, title: row.title || null, subtitle: row.subtitle || null,
        body: row.body || null, tint: row.tint, cards: row.cards || [], ctas: row.ctas || [],
        enabled: !!row.enabled, variant: row.variant || null,
      };
      await api.put(`/super-admin/website/sections/${section.id}`, payload);
      toast.success('Saved');
      setDirty(false);
      onSaved();
    } catch (_e) { toast.error('Save failed'); }
  };
  const del = async () => {
    if (!window.confirm(`Delete section "${section.section_key}"?`)) return;
    try {
      await api.delete(`/super-admin/website/sections/${section.id}`);
      toast.success('Deleted');
      onSaved();
    } catch (_e) { toast.error('Delete failed'); }
  };
  const duplicate = async () => {
    try {
      await api.post(`/super-admin/website/sections/${section.id}/duplicate`);
      toast.success('Section duplicated — edit or move the copy below.');
      onSaved();
    } catch (_e) { toast.error('Duplicate failed'); }
  };
  const fields = KIND_DEFAULT_FIELDS[row.kind] || [];

  return (
    <div className="bg-white border border-slate-200 rounded-lg" data-testid={`section-row-${section.id}`}>
      <div className="flex flex-wrap items-center gap-2 p-3">
        <button
          onClick={() => setExpanded(!expanded)}
          className={`inline-flex items-center gap-1 shrink-0 text-xs font-semibold px-2 py-1 rounded border transition-colors ${expanded ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-300 hover:border-slate-500'}`}
          data-testid={`section-toggle-${section.id}`}
          title={expanded ? 'Collapse' : 'Edit this section'}
        >
          {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          {expanded ? 'Close' : 'Edit'}
        </button>
        <span className="text-xs font-mono px-2 py-0.5 rounded bg-slate-100 text-slate-600 shrink-0">{row.kind}</span>
        <input value={row.section_key} onChange={(e) => set('section_key', e.target.value)} className="input w-32 shrink-0" />
        <input
          value={row.title || ''}
          onChange={(e) => set('title', e.target.value)}
          className="input flex-1 min-w-[140px]"
          placeholder="Title"
          data-testid={`section-title-${section.id}`}
        />
        <select value={row.tint} onChange={(e) => set('tint', e.target.value)} className="input w-24 shrink-0">{TINTS.map((t) => <option key={t}>{t}</option>)}</select>
        <input type="number" value={row.order} onChange={(e) => set('order', e.target.value)} className="input w-16 shrink-0" />
        <button onClick={() => set('enabled', !row.enabled)}
          className={row.enabled ? 'p-1.5 text-emerald-600 hover:bg-emerald-50 rounded' : 'p-1.5 text-slate-400 hover:bg-slate-100 rounded'}
          title={row.enabled ? 'Published' : 'Hidden'}>
          {row.enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
        </button>
        <button onClick={save} disabled={!dirty} className={dirty ? 'p-1.5 text-blue-600 hover:bg-blue-50 rounded' : 'p-1.5 text-slate-300'} data-testid={`section-save-${section.id}`}>
          <Save className="w-4 h-4" />
        </button>
        <button onClick={duplicate} className="p-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded" title="Duplicate this section" data-testid={`section-duplicate-${section.id}`}>
          <Copy className="w-4 h-4" />
        </button>
        <button onClick={del} className="p-1.5 text-rose-600 hover:bg-rose-50 rounded" data-testid={`section-delete-${section.id}`}><Trash2 className="w-4 h-4" /></button>
      </div>
      {expanded && (
        <div className="border-t border-slate-100 p-4 space-y-3 bg-slate-50/50">
          {fields.includes('eyebrow') && (
            <TextField label="Eyebrow" value={row.eyebrow || ''} onChange={(v) => set('eyebrow', v)} />
          )}
          {fields.includes('subtitle') && (
            <TextField label="Subtitle" value={row.subtitle || ''} onChange={(v) => set('subtitle', v)} />
          )}
          {fields.includes('body') && (
            <TextField label="Body" value={row.body || ''} onChange={(v) => set('body', v)} multiline />
          )}
          {fields.includes('variant') && (
            <label className="block">
              <span className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Built-in block</span>
              <select value={row.variant || 'pricing'} onChange={(e) => set('variant', e.target.value)} className="input" data-testid={`section-variant-${section.id}`}>
                <option value="pricing">Pricing table (live plans)</option>
                <option value="demo">Demo request form</option>
                <option value="contact">Contact form</option>
                <option value="about">About page content</option>
              </select>
            </label>
          )}
          {fields.includes('html') && (
            <HtmlField value={row.body || ''} onChange={(v) => set('body', v)} testid={`section-html-${section.id}`} />
          )}
          {fields.includes('cards') && (
            <CardsEditor cards={row.cards || []} onChange={(v) => set('cards', v)} />
          )}
          {fields.includes('ctas') && (
            <CtasEditor ctas={row.ctas || []} onChange={(v) => set('ctas', v)} />
          )}
          {/* iter337/iter343 — move this section to another page */}
          <div className="pt-3 mt-1 border-t border-slate-200 rounded-b bg-amber-50/60 -mx-4 -mb-4 px-4 pb-4">
            <div className="flex items-center gap-2 flex-wrap">
              <ArrowRightLeft className="w-4 h-4 text-amber-700 shrink-0" />
              <label className="text-xs font-semibold uppercase tracking-wider text-amber-800">Move to page</label>
              <select
                value=""
                onChange={(e) => moveToPage(e.target.value)}
                className="input w-44"
                data-testid={`section-move-${section.id}`}
              >
                <option value="">Choose destination page…</option>
                {pageOptions.filter((p) => p !== currentPage).map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
              <span className="text-[11px] text-amber-700">Currently on <strong>{currentPage}</strong></span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TextField({ label, value, onChange, multiline }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</label>
      {multiline ? (
        <textarea rows={3} value={value} onChange={(e) => onChange(e.target.value)} className="w-full input" />
      ) : (
        <input value={value} onChange={(e) => onChange(e.target.value)} className="w-full input" />
      )}
    </div>
  );
}

function CardsEditor({ cards, onChange }) {
  const update = (i, k, v) => { const copy = [...cards]; copy[i] = { ...copy[i], [k]: v }; onChange(copy); };
  const add = () => onChange([...cards, { title: '', body: '' }]);
  const remove = (i) => onChange(cards.filter((_, idx) => idx !== i));
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Cards ({cards.length})</label>
        <button type="button" onClick={add} className="text-xs px-2 py-1 rounded bg-slate-900 hover:bg-slate-800 text-white">+ Card</button>
      </div>
      <div className="space-y-2">
        {cards.map((c, i) => (
          <div key={i} className="flex gap-2 items-start bg-white border border-slate-200 rounded p-2">
            <input value={c.title} onChange={(e) => update(i, 'title', e.target.value)} className="input w-56" placeholder="Card title" />
            <textarea rows={2} value={c.body} onChange={(e) => update(i, 'body', e.target.value)} className="input flex-1" placeholder="Card body" />
            <button type="button" onClick={() => remove(i)} className="p-1.5 text-rose-600 hover:bg-rose-50 rounded"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function CtasEditor({ ctas, onChange }) {
  const update = (i, k, v) => { const copy = [...ctas]; copy[i] = { ...copy[i], [k]: v }; onChange(copy); };
  const add = () => onChange([...ctas, { label: '', href: '', variant: 'primary' }]);
  const remove = (i) => onChange(ctas.filter((_, idx) => idx !== i));
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">CTAs ({ctas.length})</label>
        <button type="button" onClick={add} className="text-xs px-2 py-1 rounded bg-slate-900 hover:bg-slate-800 text-white">+ CTA</button>
      </div>
      <div className="space-y-2">
        {ctas.map((c, i) => (
          <div key={i} className="flex gap-2 items-center bg-white border border-slate-200 rounded p-2">
            <input value={c.label} onChange={(e) => update(i, 'label', e.target.value)} className="input w-40" placeholder="Label" />
            <input value={c.href} onChange={(e) => update(i, 'href', e.target.value)} className="input flex-1" placeholder="Href" />
            <select value={c.variant} onChange={(e) => update(i, 'variant', e.target.value)} className="input w-28">
              <option>primary</option>
              <option>secondary</option>
              <option>ghost</option>
            </select>
            <button type="button" onClick={() => remove(i)} className="p-1.5 text-rose-600 hover:bg-rose-50 rounded"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------
// Pricing panel (wave 15 — Phase C)
// ------------------------------------------------------------------

function PricingPanel() {
  const [sub, setSub] = useState('plans');
  return (
    <div>
      <div className="flex gap-2 mb-4">
        {[['plans', 'Plans'], ['addons', 'Add-ons'], ['faqs', 'FAQs']].map(([k, l]) => (
          <button
            key={k}
            onClick={() => setSub(k)}
            data-testid={`pricing-subtab-${k}`}
            className={
              sub === k
                ? 'px-3 py-1.5 text-xs font-semibold rounded-full bg-slate-900 text-white'
                : 'px-3 py-1.5 text-xs rounded-full border border-slate-200 text-slate-600 hover:border-slate-400'
            }
          >
            {l}
          </button>
        ))}
      </div>
      {sub === 'plans' && <PricingPlansPanel />}
      {sub === 'addons' && <PricingAddonsPanel />}
      {sub === 'faqs' && <PricingFaqsPanel />}
    </div>
  );
}

function PricingPlansPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const emptyForm = { name: '', tagline: '', price_label: '', cadence: '/month', order: 0, highlight: false, cta_label: 'Get started', cta_href: '/contact' };
  const [f, setF] = useState(emptyForm);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/super-admin/website/pricing/plans');
      setItems(data.items || []);
    } catch (_e) { toast.error('Failed to load plans'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post('/super-admin/website/pricing/plans', { ...f, order: Number(f.order), features: [] });
      toast.success('Plan added');
      setCreating(false);
      setF(emptyForm);
      load();
    } catch (_e) { toast.error('Failed'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-600">Tier cards shown on the marketing site&apos;s Pricing page.</p>
        <button onClick={() => setCreating(true)} data-testid="plan-add-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium">
          <Plus className="w-4 h-4" /> Add plan
        </button>
      </div>
      {creating && (
        <form onSubmit={create} className="mb-4 bg-slate-50 border border-slate-200 rounded-lg p-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="plan-create-form">
          <input required placeholder="Name (Starter)" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="input" data-testid="plan-form-name" />
          <input required placeholder="Price (e.g. $99)" value={f.price_label} onChange={(e) => setF({ ...f, price_label: e.target.value })} className="input" data-testid="plan-form-price" />
          <input placeholder="Cadence (/month)" value={f.cadence} onChange={(e) => setF({ ...f, cadence: e.target.value })} className="input" />
          <input placeholder="Order" type="number" value={f.order} onChange={(e) => setF({ ...f, order: e.target.value })} className="input" />
          <input placeholder="Tagline" value={f.tagline} onChange={(e) => setF({ ...f, tagline: e.target.value })} className="input col-span-2" />
          <input placeholder="CTA label" value={f.cta_label} onChange={(e) => setF({ ...f, cta_label: e.target.value })} className="input" />
          <input placeholder="CTA href" value={f.cta_href} onChange={(e) => setF({ ...f, cta_href: e.target.value })} className="input" />
          <label className="flex items-center gap-2 text-sm text-slate-600 col-span-2">
            <input type="checkbox" checked={f.highlight} onChange={(e) => setF({ ...f, highlight: e.target.checked })} /> Highlight (Most popular)
          </label>
          <div className="col-span-2 md:col-span-4 flex gap-2">
            <button type="submit" className="px-3 py-1.5 rounded bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium" data-testid="plan-form-submit">Add</button>
            <button type="button" onClick={() => setCreating(false)} className="px-3 py-1.5 rounded text-slate-600 hover:bg-slate-100 text-sm"><X className="w-4 h-4" /></button>
          </div>
        </form>
      )}
      {loading ? (
        <div className="text-sm text-slate-400 py-8 text-center">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-slate-400 border border-dashed border-slate-200 rounded p-6 text-center">
          No plans yet — the marketing site will fall back to the built-in defaults.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((p) => <PlanRow key={p.id} plan={p} onSaved={load} />)}
        </div>
      )}
      <style>{`.input { padding:6px 10px;border:1px solid #E2E8F0;border-radius:8px;font-size:13px;background:#fff;color:#0f172a }`}</style>
    </div>
  );
}

function PlanRow({ plan, onSaved }) {
  const [row, setRow] = useState({ ...plan, features_text: (plan.features || []).join('\n') });
  const [dirty, setDirty] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const set = (k, v) => { setRow({ ...row, [k]: v }); setDirty(true); };

  const save = async () => {
    try {
      const features = row.features_text.split('\n').map((s) => s.trim()).filter(Boolean);
      const payload = {
        name: row.name, tagline: row.tagline || null, price_label: row.price_label,
        cadence: row.cadence || null, features, cta_label: row.cta_label, cta_href: row.cta_href,
        highlight: !!row.highlight, order: Number(row.order), enabled: !!row.enabled,
      };
      await api.put(`/super-admin/website/pricing/plans/${plan.id}`, payload);
      toast.success('Saved');
      setDirty(false);
      onSaved();
    } catch (_e) { toast.error('Save failed'); }
  };
  const del = async () => {
    if (!window.confirm(`Delete plan "${plan.name}"?`)) return;
    try {
      await api.delete(`/super-admin/website/pricing/plans/${plan.id}`);
      toast.success('Deleted'); onSaved();
    } catch (_e) { toast.error('Delete failed'); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg" data-testid={`plan-row-${plan.id}`}>
      <div className="flex items-center gap-2 p-3 flex-wrap">
        <button onClick={() => setExpanded(!expanded)} className="text-slate-400 hover:text-slate-700 w-6 text-left">{expanded ? '▾' : '▸'}</button>
        <input value={row.name} onChange={(e) => set('name', e.target.value)} className="input w-32 font-medium" />
        <input value={row.price_label} onChange={(e) => set('price_label', e.target.value)} className="input w-24" />
        <input value={row.cadence || ''} onChange={(e) => set('cadence', e.target.value)} className="input w-20" />
        <input value={row.tagline || ''} onChange={(e) => set('tagline', e.target.value)} className="input flex-1 min-w-[200px]" placeholder="Tagline" />
        <label className="flex items-center gap-1 text-xs text-slate-600 whitespace-nowrap">
          <input type="checkbox" checked={row.highlight} onChange={(e) => set('highlight', e.target.checked)} /> Featured
        </label>
        <input type="number" value={row.order} onChange={(e) => set('order', e.target.value)} className="input w-16" />
        <button onClick={() => set('enabled', !row.enabled)}
          className={row.enabled ? 'p-1.5 text-emerald-600 hover:bg-emerald-50 rounded' : 'p-1.5 text-slate-400 hover:bg-slate-100 rounded'}
          title={row.enabled ? 'Published' : 'Hidden'}>
          {row.enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
        </button>
        <button onClick={save} disabled={!dirty} className={dirty ? 'p-1.5 text-blue-600 hover:bg-blue-50 rounded' : 'p-1.5 text-slate-300'} data-testid={`plan-save-${plan.id}`}>
          <Save className="w-4 h-4" />
        </button>
        <button onClick={del} className="p-1.5 text-rose-600 hover:bg-rose-50 rounded" data-testid={`plan-delete-${plan.id}`}><Trash2 className="w-4 h-4" /></button>
      </div>
      {expanded && (
        <div className="border-t border-slate-100 p-4 space-y-3 bg-slate-50/50">
          <TextField label="Features (one per line)" value={row.features_text} onChange={(v) => set('features_text', v)} multiline />
          <div className="grid grid-cols-2 gap-3">
            <TextField label="CTA label" value={row.cta_label} onChange={(v) => set('cta_label', v)} />
            <TextField label="CTA href" value={row.cta_href} onChange={(v) => set('cta_href', v)} />
          </div>
        </div>
      )}
    </div>
  );
}

function PricingAddonsPanel() {
  return <SimpleListPanel
    collectionPath="/super-admin/website/pricing/addons"
    label="Add-ons"
    testIdPrefix="addon"
    fields={{ title: 'Title', body: 'Body' }}
    multilineFields={{ body: true }}
  />;
}

function PricingFaqsPanel() {
  return <SimpleListPanel
    collectionPath="/super-admin/website/pricing/faqs"
    label="FAQs"
    testIdPrefix="faq"
    fields={{ question: 'Question', answer: 'Answer' }}
    multilineFields={{ answer: true }}
  />;
}

/** Generic two-field list panel used for both add-ons and FAQs. */
function SimpleListPanel({ collectionPath, label, testIdPrefix, fields, multilineFields = {} }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const fieldKeys = Object.keys(fields);
  const emptyForm = { order: 0, ...Object.fromEntries(fieldKeys.map((k) => [k, ''])) };
  const [f, setF] = useState(emptyForm);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(collectionPath);
      setItems(data.items || []);
    } catch (_e) { toast.error(`Failed to load ${label}`); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [collectionPath]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post(collectionPath, { ...f, order: Number(f.order) });
      toast.success('Added');
      setCreating(false);
      setF(emptyForm);
      load();
    } catch (_e) { toast.error('Failed'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-600">Marketing site&apos;s {label.toLowerCase()} section.</p>
        <button onClick={() => setCreating(true)} data-testid={`${testIdPrefix}-add-btn`}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium">
          <Plus className="w-4 h-4" /> Add {label.slice(0, -1)}
        </button>
      </div>
      {creating && (
        <form onSubmit={create} className="mb-4 bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-3" data-testid={`${testIdPrefix}-create-form`}>
          {fieldKeys.map((k) => (
            <div key={k}>
              <label className="block text-xs text-slate-600 mb-1">{fields[k]}</label>
              {multilineFields[k]
                ? <textarea required rows={3} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="input w-full" data-testid={`${testIdPrefix}-form-${k}`} />
                : <input required value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="input w-full" data-testid={`${testIdPrefix}-form-${k}`} />
              }
            </div>
          ))}
          <div className="flex gap-2 items-end">
            <div>
              <label className="block text-xs text-slate-600 mb-1">Order</label>
              <input type="number" value={f.order} onChange={(e) => setF({ ...f, order: e.target.value })} className="input w-20" />
            </div>
            <button type="submit" className="px-3 py-1.5 rounded bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium" data-testid={`${testIdPrefix}-form-submit`}>Add</button>
            <button type="button" onClick={() => setCreating(false)} className="px-3 py-1.5 rounded text-slate-600 hover:bg-slate-100 text-sm"><X className="w-4 h-4" /></button>
          </div>
        </form>
      )}
      {loading ? (
        <div className="text-sm text-slate-400 py-8 text-center">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-slate-400 border border-dashed border-slate-200 rounded p-6 text-center">No {label.toLowerCase()} yet — defaults will be used on the marketing site.</div>
      ) : (
        <div className="space-y-2">
          {items.map((it) => (
            <SimpleRow
              key={it.id}
              item={it}
              fieldKeys={fieldKeys}
              fields={fields}
              multilineFields={multilineFields}
              collectionPath={collectionPath}
              onSaved={load}
              testIdPrefix={testIdPrefix}
            />
          ))}
        </div>
      )}
      <style>{`.input { padding:6px 10px;border:1px solid #E2E8F0;border-radius:8px;font-size:13px;background:#fff;color:#0f172a }`}</style>
    </div>
  );
}

function SimpleRow({ item, fieldKeys, fields, multilineFields, collectionPath, onSaved, testIdPrefix }) {
  const [row, setRow] = useState(item);
  const [dirty, setDirty] = useState(false);
  const set = (k, v) => { setRow({ ...row, [k]: v }); setDirty(true); };
  const save = async () => {
    try {
      const payload = { order: Number(row.order), enabled: !!row.enabled };
      fieldKeys.forEach((k) => { payload[k] = row[k] || ''; });
      await api.put(`${collectionPath}/${item.id}`, payload);
      toast.success('Saved'); setDirty(false); onSaved();
    } catch (_e) { toast.error('Save failed'); }
  };
  const del = async () => {
    if (!window.confirm('Delete?')) return;
    try { await api.delete(`${collectionPath}/${item.id}`); toast.success('Deleted'); onSaved(); }
    catch (_e) { toast.error('Delete failed'); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3 space-y-2" data-testid={`${testIdPrefix}-row-${item.id}`}>
      {fieldKeys.map((k) => (
        <div key={k}>
          <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-0.5">{fields[k]}</label>
          {multilineFields[k]
            ? <textarea rows={2} value={row[k] || ''} onChange={(e) => set(k, e.target.value)} className="input w-full" />
            : <input value={row[k] || ''} onChange={(e) => set(k, e.target.value)} className="input w-full" />
          }
        </div>
      ))}
      <div className="flex items-center justify-end gap-2">
        <input type="number" value={row.order} onChange={(e) => set('order', e.target.value)} className="input w-16" title="Order" />
        <button onClick={() => set('enabled', !row.enabled)}
          className={row.enabled ? 'p-1.5 text-emerald-600 hover:bg-emerald-50 rounded' : 'p-1.5 text-slate-400 hover:bg-slate-100 rounded'}>
          {row.enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
        </button>
        <button onClick={save} disabled={!dirty} className={dirty ? 'p-1.5 text-blue-600 hover:bg-blue-50 rounded' : 'p-1.5 text-slate-300'} data-testid={`${testIdPrefix}-save-${item.id}`}>
          <Save className="w-4 h-4" />
        </button>
        <button onClick={del} className="p-1.5 text-rose-600 hover:bg-rose-50 rounded" data-testid={`${testIdPrefix}-delete-${item.id}`}><Trash2 className="w-4 h-4" /></button>
      </div>
    </div>
  );
}


// ------------------------------------------------------------------
// Site Settings panel (iter336) — brand logo, tagline, contact info,
// social links. Rendered on the marketing shell + Contact page.
// ------------------------------------------------------------------

function SiteSettingsPanel() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/super-admin/website/settings')
      .then(({ data }) => setForm({
        logo_url: data.logo_url || '',
        logo_text: data.logo_text || '',
        footer_tagline: data.footer_tagline || '',
        contact_email: data.contact_email || '',
        contact_phone: data.contact_phone || '',
        contact_address: data.contact_address || '',
        social: {
          twitter: data.social?.twitter || '',
          linkedin: data.social?.linkedin || '',
          facebook: data.social?.facebook || '',
          instagram: data.social?.instagram || '',
          youtube: data.social?.youtube || '',
        },
        cookie_notice: data.cookie_notice || {},
      }))
      .catch(() => toast.error('Failed to load site settings'));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setSocial = (k) => (e) => setForm((f) => ({ ...f, social: { ...f.social, [k]: e.target.value } }));

  const [uploading, setUploading] = useState(false);
  const uploadLogo = async (e) => {
    const fileObj = e.target.files?.[0];
    if (!fileObj) return;
    if (fileObj.size > 5 * 1024 * 1024) { toast.error('File too large (max 5 MB)'); return; }
    const fd = new FormData();
    fd.append('file', fileObj);
    setUploading(true);
    try {
      const { data } = await api.post('/super-admin/website/upload-media', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm((f) => ({ ...f, logo_url: data.url }));
      toast.success('Logo uploaded. Click "Save settings" to publish it.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); e.target.value = ''; }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/super-admin/website/settings', form);
      toast.success('Site settings saved. Public pages update on next load.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  if (!form) return <div className="text-sm text-slate-500" data-testid="site-settings-loading">Loading…</div>;

  const inputCls = 'w-full border border-slate-300 rounded px-3 py-2 text-sm bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10';

  return (
    <div className="max-w-3xl space-y-8" data-testid="site-settings-panel">
      {/* Brand */}
      <section>
        <h2 className="text-sm font-semibold text-slate-900 mb-3">Brand</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Logo image URL</label>
            <input value={form.logo_url} onChange={set('logo_url')} placeholder="https://…/logo.png" className={inputCls} data-testid="settings-logo-url" />
            <div className="flex items-center gap-2 mt-2">
              <label className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 cursor-pointer px-3 py-1.5 rounded" data-testid="settings-logo-upload-label">
                <Upload className="w-3.5 h-3.5" />
                {uploading ? 'Uploading…' : 'Upload image'}
                <input type="file" accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml" onChange={uploadLogo} disabled={uploading} className="hidden" data-testid="settings-logo-upload-input" />
              </label>
              <span className="text-[11px] text-slate-400">or paste a URL above · PNG/JPG/SVG/WEBP, max 5 MB</span>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Wordmark text</label>
            <input value={form.logo_text} onChange={set('logo_text')} placeholder="VyomOS" className={inputCls} data-testid="settings-logo-text" />
            <p className="text-[11px] text-slate-400 mt-1">Shown when no logo image is set.</p>
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-slate-600 mb-1">Footer tagline</label>
            <input value={form.footer_tagline} onChange={set('footer_tagline')} placeholder="Software with room to grow." className={inputCls} data-testid="settings-footer-tagline" />
          </div>
        </div>
        {(form.logo_url || form.logo_text) && (
          <div className="mt-3 flex items-center gap-2 p-3 bg-slate-50 border border-slate-200 rounded" data-testid="settings-logo-preview">
            {form.logo_url
              ? <img src={form.logo_url} alt="logo preview" className="h-8 w-auto max-w-[160px] object-contain" />
              : <span className="text-lg font-semibold">{form.logo_text}</span>}
            <span className="text-xs text-slate-400 ml-2">Preview</span>
          </div>
        )}
      </section>

      {/* Contact */}
      <section>
        <h2 className="text-sm font-semibold text-slate-900 mb-3">Contact information</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Email</label>
            <input value={form.contact_email} onChange={set('contact_email')} placeholder="hello@vyomos.io" className={inputCls} data-testid="settings-contact-email" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Phone</label>
            <input value={form.contact_phone} onChange={set('contact_phone')} placeholder="+1 (415) 555-0100" className={inputCls} data-testid="settings-contact-phone" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-slate-600 mb-1">Address</label>
            <input value={form.contact_address} onChange={set('contact_address')} placeholder="San Francisco · Remote-first" className={inputCls} data-testid="settings-contact-address" />
          </div>
        </div>
      </section>

      {/* Social */}
      <section>
        <h2 className="text-sm font-semibold text-slate-900 mb-3">Social links</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {['twitter', 'linkedin', 'facebook', 'instagram', 'youtube'].map((k) => (
            <div key={k}>
              <label className="block text-xs font-medium text-slate-600 mb-1 capitalize">{k}</label>
              <input value={form.social[k]} onChange={setSocial(k)} placeholder={`https://${k}.com/…`} className={inputCls} data-testid={`settings-social-${k}`} />
            </div>
          ))}
        </div>
      </section>

      <CookieNoticeSettings value={form.cookie_notice} onChange={(v) => setForm((f) => ({ ...f, cookie_notice: v }))} inputCls={inputCls} />

      <div>
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded" data-testid="settings-save-btn">
          <Save className="w-4 h-4" />
          {saving ? 'Saving…' : 'Save settings'}
        </button>
      </div>
    </div>
  );
}


// ------------------------------------------------------------------
// Pages panel (iter336) — add / remove / reorder marketing pages.
// System pages can be reordered / hidden / renamed but not deleted.
// Custom pages render generically from their CMS sections.
// ------------------------------------------------------------------

function PagesPanel() {
  const [pages, setPages] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ slug: '', title: '', nav_label: '' });
  const [seoOpen, setSeoOpen] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get('/super-admin/website/pages');
      setPages(data.pages || []);
    } catch { toast.error('Failed to load pages'); }
  };
  useEffect(() => { load(); }, []);

  const createPage = async () => {
    if (!form.slug || !form.title) { toast.error('Slug and title are required'); return; }
    setCreating(true);
    try {
      await api.post('/super-admin/website/pages', form);
      toast.success('Page created. Add sections to it in the Page Sections tab.');
      setForm({ slug: '', title: '', nav_label: '' });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Create failed'); }
    finally { setCreating(false); }
  };

  const toggleStatus = async (p) => {
    try {
      await api.put(`/super-admin/website/pages/${p.id}`, { status: p.status === 'published' ? 'draft' : 'published' });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  };

  const rename = async (p, field, value) => {
    try { await api.put(`/super-admin/website/pages/${p.id}`, { [field]: value }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete page "${p.title}" and all its sections? This cannot be undone.`)) return;
    try { await api.delete(`/super-admin/website/pages/${p.id}`); toast.success('Page deleted'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  const move = async (idx, dir) => {
    const arr = [...pages];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setPages(arr);
    try { await api.post('/super-admin/website/pages/reorder', { ordered_ids: arr.map((p) => p.id) }); }
    catch { toast.error('Reorder failed'); load(); }
  };

  if (!pages) return <div className="text-sm text-slate-500" data-testid="pages-loading">Loading…</div>;

  const inputCls = 'border border-slate-300 rounded px-2 py-1 text-sm bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10';

  return (
    <div className="max-w-4xl space-y-6" data-testid="pages-panel">
      {/* Add page */}
      <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
        <h2 className="text-sm font-semibold text-slate-900 mb-3">Add a new page</h2>
        <div className="grid md:grid-cols-4 gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Slug (URL)</label>
            <input value={form.slug} onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))} placeholder="why-us" className={`${inputCls} w-full`} data-testid="page-new-slug" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Page title</label>
            <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Why Us" className={`${inputCls} w-full`} data-testid="page-new-title" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Nav label (optional)</label>
            <input value={form.nav_label} onChange={(e) => setForm((f) => ({ ...f, nav_label: e.target.value }))} placeholder="Why Us" className={`${inputCls} w-full`} data-testid="page-new-nav-label" />
          </div>
          <button onClick={createPage} disabled={creating} className="inline-flex items-center justify-center gap-1.5 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded" data-testid="page-create-btn">
            <Plus className="w-4 h-4" /> {creating ? 'Adding…' : 'Add page'}
          </button>
        </div>
        <p className="text-[11px] text-slate-400 mt-2">Every page lives at /&lt;slug&gt; and renders the sections you add in the Page Sections tab (built-in pages start with one movable "builtin" block). Use the search icon on a row for its meta title, description, canonical URL and indexing.</p>
      </div>

      {/* Page list */}
      <div className="space-y-2">
        {pages.map((p, idx) => (
          <div key={p.id} className="border border-slate-200 rounded-lg px-3 py-2" data-testid={`page-row-${p.slug}`}>
          <div className="flex items-center gap-3">
            <div className="flex flex-col">
              <button onClick={() => move(idx, -1)} disabled={idx === 0} className="text-slate-400 hover:text-slate-700 disabled:opacity-30 leading-none" data-testid={`page-up-${p.slug}`}>▲</button>
              <button onClick={() => move(idx, 1)} disabled={idx === pages.length - 1} className="text-slate-400 hover:text-slate-700 disabled:opacity-30 leading-none" data-testid={`page-down-${p.slug}`}>▼</button>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <input defaultValue={p.title} onBlur={(e) => e.target.value !== p.title && rename(p, 'title', e.target.value)} className={`${inputCls} font-medium`} data-testid={`page-title-${p.slug}`} />
                <code className="text-xs text-slate-400">/{p.slug}</code>
                {p.is_system && <span className="text-[10px] uppercase tracking-wide bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded" data-testid={`page-system-badge-${p.slug}`}>System</span>}
              </div>
            </div>
            <button onClick={() => toggleStatus(p)} className={`text-xs px-2 py-1 rounded ${p.status === 'published' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`} data-testid={`page-status-${p.slug}`}>
              {p.status === 'published' ? 'Published' : 'Draft'}
            </button>
            {p.noindex && <span className="text-[10px] uppercase bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded">noindex</span>}
            <SeoToggle open={seoOpen === p.id} onClick={() => setSeoOpen(seoOpen === p.id ? null : p.id)} slug={p.slug} />
            {p.slug !== 'home' && (
              <button onClick={() => del(p)} className="p-1.5 text-rose-600 hover:bg-rose-50 rounded" data-testid={`page-delete-${p.slug}`}><Trash2 className="w-4 h-4" /></button>
            )}
          </div>
          {seoOpen === p.id && <PageSeoEditor page={p} onSaved={load} />}
          </div>
        ))}
      </div>
    </div>
  );
}

